import { Host, forceUpdate, h } from "@stencil/core";
import { inBetween, isRTL, tokenMap } from "../../common/utils";
import { BUTTON_CLASS, CAPTION_ID, CLOSE_BUTTON_PART, DRAG_PREVIEW, DRAG_PREVIEW_ELEMENT, DRAG_PREVIEW_INSIDE_BLOCK, DRAG_PREVIEW_INSIDE_INLINE, DRAG_PREVIEW_OUTSIDE, IMAGE_CLASS, PAGE_CLASS, PAGE_CONTAINER_CLASS, PAGE_ID, PAGE_NAME_CLASS, SELECTED_PART, TAB_LIST_CLASS } from "./utils";
import { insertIntoIndex, removeElement } from "../../common/array";
import { focusComposedPath } from "../common/helpers";
// Custom vars
const TRANSITION_DURATION = "--ch-tab-transition-duration";
const BUTTON_POSITION_X = "--ch-tab-button-position-x";
const BUTTON_POSITION_Y = "--ch-tab-button-position-y";
const BUTTON_SIZE = "--ch-tab-button-size";
const MOUSE_OFFSET_X = "--ch-tab-mouse-offset-x";
const MOUSE_OFFSET_Y = "--ch-tab-mouse-offset-y";
const MOUSE_POSITION_X = "--ch-tab-mouse-position-x";
const MOUSE_POSITION_Y = "--ch-tab-mouse-position-y";
const TAB_LIST_EDGE_START_POSITION = "--ch-tab-tab-list-start";
const TAB_LIST_EDGE_END_POSITION = "--ch-tab-tab-list-end";
const DECORATIVE_IMAGE = "--ch-tab-decorative-image";
// Key codes
const ARROW_UP = "ArrowUp";
const ARROW_RIGHT = "ArrowRight";
const ARROW_DOWN = "ArrowDown";
const ARROW_LEFT = "ArrowLeft";
const HOME = "Home";
const END = "End";
// Selectors
const FIRST_CAPTION_BUTTON = (tabListRef) => tabListRef.querySelector(":scope>button");
const LAST_CAPTION_BUTTON = (tabListRef) => tabListRef.querySelector(":scope>button:last-child");
// Utility functions
const isDecorativeImg = (item) => item.startImageSrc &&
    (!item.startImageType || item.startImageType === "pseudo-element");
const getDirection = (type) => type === "main" || type === "blockEnd" ? "block" : "inline";
const isBlockDirection = (direction) => direction === "block";
const setProperty = (element, property, value) => element.style.setProperty(property, `${value}px`);
const setButtonInitialPosition = (element, positionX, positionY) => {
    setProperty(element, BUTTON_POSITION_X, positionX);
    setProperty(element, BUTTON_POSITION_Y, positionY);
};
const setButtonSize = (element, size) => {
    setProperty(element, BUTTON_SIZE, size);
};
const setMousePosition = (element, positionX, positionY) => {
    setProperty(element, MOUSE_POSITION_X, positionX);
    setProperty(element, MOUSE_POSITION_Y, positionY);
};
// Useful to implement snap to the edges
const setTabListStartEndPosition = (element, startPosition, endPosition) => {
    setProperty(element, TAB_LIST_EDGE_START_POSITION, startPosition);
    setProperty(element, TAB_LIST_EDGE_END_POSITION, endPosition);
};
const getTabListSizesAndSetPosition = (hostRef, tabListRef, direction, buttonRect) => {
    const tabListRect = tabListRef.getBoundingClientRect();
    // Tab List information
    const tabListSizes = {
        xStart: tabListRect.x,
        xEnd: tabListRect.x + tabListRect.width,
        yStart: tabListRect.y,
        yEnd: tabListRect.y + tabListRect.height
    };
    if (isBlockDirection(direction)) {
        setTabListStartEndPosition(hostRef, tabListSizes.xStart, tabListSizes.xEnd - buttonRect.width);
    }
    else {
        setTabListStartEndPosition(hostRef, tabListSizes.yStart, tabListSizes.yEnd - buttonRect.height);
    }
    return tabListSizes;
};
const setMouseOffset = (element, offsetX, offsetY) => {
    setProperty(element, MOUSE_OFFSET_X, offsetX);
    setProperty(element, MOUSE_OFFSET_Y, offsetY);
};
const addGrabbingStyle = () => document.body.style.setProperty("cursor", "grabbing");
const removeGrabbingStyle = () => document.body.style.removeProperty("cursor");
const focusNextOrPreviousCaption = (focusNextSibling, tabListRef, focusedCaption, event) => {
    // Prevent scroll
    event.preventDefault();
    let nextFocusedCaption;
    // Determine the next selected caption button
    if (focusNextSibling) {
        nextFocusedCaption = (focusedCaption.nextElementSibling ??
            FIRST_CAPTION_BUTTON(tabListRef));
    }
    else {
        nextFocusedCaption = (focusedCaption.previousElementSibling ??
            LAST_CAPTION_BUTTON(tabListRef));
    }
    // Focus and select the caption
    nextFocusedCaption.focus();
    nextFocusedCaption.click();
};
export class ChTab {
    constructor() {
        this.draggedElementIndex = -1;
        this.draggedElementNewIndex = -1;
        this.hasCrossedBoundaries = false;
        this.accessibleName = undefined;
        this.closeButtonAccessibleName = "Close";
        this.dragDisabled = false;
        this.expanded = true;
        this.items = undefined;
        this.selectedId = undefined;
        this.type = undefined;
    }
    #cancelId;
    // Styling
    #classes = {};
    #parts = {};
    #direction;
    #selectedIndex = -1;
    #showCaptions;
    #lastDragEvent;
    #needForRAF = true; // To prevent redundant RAF (request animation frame) calls
    #initialMousePosition = -1;
    // Allocated at runtime to reduce memory usage
    #itemSizes;
    /**
     * This variable represents the boundaries of the box where the mouse can be
     * placed when dragging a caption, to consider that the caption is within the
     * tab list.
     */
    #mouseBoundingLimits;
    #renderedPages = new Set();
    // Refs
    #dragPreviewRef;
    #tabListRef;
    #tabPageRef;
    // Keyboard interactions
    #keyEvents = {
        [ARROW_UP]: (direction, ev, focusedButton) => {
            if (direction === "block") {
                return;
            }
            focusNextOrPreviousCaption(false, this.#tabListRef, focusedButton, ev);
        },
        [ARROW_RIGHT]: (direction, ev, focusedButton) => {
            if (direction === "inline") {
                return;
            }
            focusNextOrPreviousCaption(!isRTL(), this.#tabListRef, focusedButton, ev);
        },
        [ARROW_DOWN]: (direction, ev, focusedButton) => {
            if (direction === "block") {
                return;
            }
            focusNextOrPreviousCaption(true, this.#tabListRef, focusedButton, ev);
        },
        [ARROW_LEFT]: (direction, ev, focusedButton) => {
            if (direction === "inline") {
                return;
            }
            focusNextOrPreviousCaption(isRTL(), this.#tabListRef, focusedButton, ev);
        },
        [HOME]: () => {
            const nextFocusedCaption = FIRST_CAPTION_BUTTON(this.#tabListRef);
            // Focus and select the caption
            nextFocusedCaption.focus();
            nextFocusedCaption.click();
        },
        [END]: () => {
            const nextFocusedCaption = LAST_CAPTION_BUTTON(this.#tabListRef);
            // Focus and select the caption
            nextFocusedCaption.focus();
            nextFocusedCaption.click();
        }
    };
    el;
    handleItemsChange(newItems) {
        this.#updateRenderedPages(newItems);
    }
    handleSelectedIdChange(newSelectedId) {
        this.#renderedPages.add(newSelectedId);
    }
    handleTypeChange(newType) {
        this.#initializeState(newType);
    }
    /**
     * Fired when an item of the main group is double clicked.
     */
    expandMainGroup;
    /**
     * Fired the close button of an item is clicked.
     */
    itemClose;
    /**
     * Fired when the selected item change.
     */
    selectedItemChange;
    /**
     * Fired the first time a caption button is dragged outside of its tab list.
     */
    itemDragStart;
    /**
     * Ends the preview of the dragged item. Useful for ending the preview via
     * keyboard interaction.
     */
    async endDragPreview() {
        this.#handleDragEnd();
    }
    /**
     * Returns the info associated to the draggable view.
     */
    async getDraggableViews() {
        return {
            mainView: this.el,
            pageView: this.#tabPageRef,
            tabListView: this.#tabListRef
        };
    }
    /**
     * Promotes the drag preview to the top layer. Useful to avoid z-index issues.
     */
    async promoteDragPreviewToTopLayer() {
        if (this.draggedElementIndex === -1) {
            return;
        }
        // If this property is added in a declarative way via the Stencil's render,
        // we would have to use requestAnimationFrame to delay the shopPopover()
        // method, since the popover defaults to "auto", which does not allow to
        // keep multiple "auto" popover open at the same time
        this.#dragPreviewRef.popover = "manual";
        this.#dragPreviewRef.showPopover();
    }
    /**
     * Given an id, remove the page from the render
     */
    async removePage(pageId, forceRerender = true) {
        this.#renderedPages.delete(pageId);
        if (forceRerender) {
            forceUpdate(this);
        }
    }
    #handleSelectedItemChange = (index, itemId) => (event) => {
        event.stopPropagation();
        this.selectedItemChange.emit({
            lastSelectedIndex: this.#selectedIndex,
            newSelectedId: itemId,
            newSelectedIndex: index,
            type: this.type
        });
        this.#selectedIndex = index;
        // this.selectedId = itemId;
    };
    /**
     * Make a set based on the rendered items array to maintain order between the
     * pages, even when re-ordering tabs. This is useful for optimizing rendering
     * performance by not re-ordering pages when the caption's order changes.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #updateRenderedPages = (items) => {
        (items ?? []).forEach(item => {
            if (item.wasRendered) {
                this.#renderedPages.add(item.id);
            }
        });
    };
    #handleItemDblClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.expandMainGroup.emit();
    };
    #handleDragStart = (index) => (event) => {
        // Remove dragover event to allow mousemove event to fire
        event.preventDefault();
        // Store the index of the dragged element
        this.draggedElementIndex = index;
        // - - - - - - - - - - - DOM read operations - - - - - - - - - - -
        const mousePositionX = event.clientX;
        const mousePositionY = event.clientY;
        const getItemSize = isBlockDirection(this.#direction)
            ? (item) => item.getBoundingClientRect().width
            : (item) => item.getBoundingClientRect().height;
        this.#itemSizes = [...this.#tabListRef.children].map(getItemSize);
        const buttonRect = event.target.getBoundingClientRect();
        // Tab List information
        const tabListSizes = getTabListSizesAndSetPosition(this.el, this.#tabListRef, this.#direction, buttonRect);
        // Button information
        const buttonSizes = {
            xStart: buttonRect.x,
            xEnd: buttonRect.x + buttonRect.width,
            yStart: buttonRect.y,
            yEnd: buttonRect.y + buttonRect.height
        };
        const mouseDistanceToButtonTopEdge = mousePositionY - buttonSizes.yStart;
        const mouseDistanceToButtonBottomEdge = buttonSizes.yEnd - mousePositionY;
        const mouseDistanceToButtonLeftEdge = mousePositionX - buttonSizes.xStart;
        const mouseDistanceToButtonRightEdge = buttonSizes.xEnd - mousePositionX;
        // Mouse limits
        this.#mouseBoundingLimits = {
            xStart: tabListSizes.xStart - mouseDistanceToButtonRightEdge,
            xEnd: tabListSizes.xEnd + mouseDistanceToButtonLeftEdge,
            yStart: tabListSizes.yStart - mouseDistanceToButtonBottomEdge,
            yEnd: tabListSizes.yEnd + mouseDistanceToButtonTopEdge
        };
        // Store initial mouse position
        this.#initialMousePosition = isBlockDirection(this.#direction)
            ? mousePositionX
            : mousePositionY;
        // - - - - - - - - - - - DOM write operations - - - - - - - - - - -
        // Initialize mouse position to avoid initial flickering
        setMousePosition(this.el, mousePositionX, mousePositionY);
        // Initialize the button position
        setButtonInitialPosition(this.el, buttonSizes.xStart, buttonSizes.yStart);
        setButtonSize(this.el, isBlockDirection(this.#direction) ? buttonRect.width : buttonRect.height);
        // Update mouse offset to correctly place the dragged element preview
        setMouseOffset(this.el, mouseDistanceToButtonLeftEdge, mouseDistanceToButtonTopEdge);
        addGrabbingStyle();
        // Add listeners
        document.body.addEventListener("mousemove", this.#handleItemDrag, {
            capture: true,
            passive: true
        });
        document.body.addEventListener("mouseup", this.#handleDragEnd, {
            capture: true
        });
    };
    #handleDragEnd = () => {
        // Since mousemove callbacks are executed on animation frames, we must also
        // remove the events on animations frame. Otherwise we would remove the
        // events and in the next frame the mousemove handler will be executes
        cancelAnimationFrame(this.#cancelId);
        this.#needForRAF = true;
        document.body.removeEventListener("mousemove", this.#handleItemDrag, {
            capture: true
        });
        document.body.removeEventListener("mouseup", this.#handleDragEnd, {
            capture: true
        });
        removeGrabbingStyle();
        const anItemWasReordered = !this.hasCrossedBoundaries &&
            this.draggedElementNewIndex !== this.draggedElementIndex;
        // Move the item to the new position
        if (anItemWasReordered) {
            const itemToInsert = removeElement(this.items, this.draggedElementIndex);
            insertIntoIndex(this.items, itemToInsert, this.draggedElementNewIndex);
            // Update last selected index
            this.adjustLastSelectedIndexValueAfterReorder();
        }
        // Restore visibility of the dragged element
        this.draggedElementIndex = -1;
        this.draggedElementNewIndex = -1;
        // Free the memory
        this.#itemSizes = undefined;
        // Reset state
        this.hasCrossedBoundaries = false;
        this.el.style.removeProperty(TRANSITION_DURATION);
    };
    adjustLastSelectedIndexValueAfterReorder() {
        // If the dragged element is the selected element, use the new index
        if (this.#selectedIndex === this.draggedElementIndex) {
            this.#selectedIndex = this.draggedElementNewIndex;
        }
        // Dragged element:
        //   - Started: Before the selected index
        //   - Ended: After the selected index or in the same position
        else if (this.draggedElementIndex < this.#selectedIndex &&
            this.#selectedIndex <= this.draggedElementNewIndex) {
            this.#selectedIndex--;
        }
        // Dragged element:
        //   - Started: After the selected index
        //   - Ended: Before the selected index or in the same position
        else if (this.#selectedIndex < this.draggedElementIndex &&
            this.draggedElementNewIndex <= this.#selectedIndex) {
            this.#selectedIndex++;
        }
    }
    #handleItemDrag = (event) => {
        this.#lastDragEvent = event;
        if (!this.#needForRAF) {
            return;
        }
        this.#needForRAF = false; // No need to call RAF up until next frame
        this.#cancelId = requestAnimationFrame(() => {
            this.#needForRAF = true; // RAF now consumes the movement instruction so a new one can come
            const mousePositionX = this.#lastDragEvent.clientX;
            const mousePositionY = this.#lastDragEvent.clientY;
            setMousePosition(this.el, mousePositionX, mousePositionY);
            // There is no need to update the preview of the reorder
            if (this.hasCrossedBoundaries) {
                return;
            }
            const mouseLimits = this.#mouseBoundingLimits;
            const draggedButtonIsInsideTheTabList = inBetween(mouseLimits.xStart, mousePositionX, mouseLimits.xEnd) &&
                inBetween(mouseLimits.yStart, mousePositionY, mouseLimits.yEnd);
            // Emit the itemDragStart event the first time the button is out of the
            // mouse bounds (`mouseBoundingLimits`)
            if (!draggedButtonIsInsideTheTabList) {
                this.hasCrossedBoundaries = true;
                // Remove transition before the render to avoid flickering in the animation
                this.el.style.setProperty(TRANSITION_DURATION, "0s");
                this.itemDragStart.emit(this.draggedElementIndex);
                return;
            }
            // There is no need to re-order the items in the preview
            if (this.items.length === 1) {
                return;
            }
            // In this point, the preview is inside the tab list, we should check
            // in which place is the preview to give feedback for the item's reorder
            const mousePosition = isBlockDirection(this.#direction)
                ? mousePositionX
                : mousePositionY;
            const hasMovedToTheEnd = this.#initialMousePosition < mousePosition;
            // Distance traveled from the initial mouse position
            let distanceTraveled = Math.abs(this.#initialMousePosition - mousePosition);
            let newIndex = this.draggedElementIndex;
            if (hasMovedToTheEnd) {
                // While it is not the last item and the distance traveled is greater
                // than half the size of the next item
                while (newIndex < this.items.length - 1 &&
                    distanceTraveled - this.#itemSizes[newIndex + 1] / 2 > 0) {
                    distanceTraveled -= this.#itemSizes[newIndex + 1];
                    newIndex++;
                }
            }
            else {
                // While it is not the first item and the distance traveled is greater
                // than half the size of the previous item
                while (newIndex > 0 &&
                    distanceTraveled - this.#itemSizes[newIndex - 1] / 2 > 0) {
                    distanceTraveled -= this.#itemSizes[newIndex - 1];
                    newIndex--;
                }
            }
            // Check if should update the dragged element index in the preview
            if (this.draggedElementNewIndex !== newIndex) {
                this.draggedElementNewIndex = newIndex;
            }
        });
    };
    #handleClose = (index, itemId) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.itemClose.emit({
            itemIndex: index,
            itemId: itemId,
            type: this.type
        });
    };
    #handleTabFocus = (event) => {
        if (this.items.length < 2) {
            return;
        }
        const keyEventHandler = this.#keyEvents[event.code];
        if (!keyEventHandler) {
            return;
        }
        const currentFocusedCaption = focusComposedPath()[0].closest("." + this.#classes.BUTTON);
        keyEventHandler(this.#direction, event, currentFocusedCaption);
    };
    #imgRender = (item) => item.startImageType === "img" &&
        item.startImageSrc && (h("img", { "aria-hidden": "true", class: "caption-image " + this.#classes.IMAGE, part: this.#parts.IMAGE, alt: "", src: item.startImageSrc, loading: "lazy" }));
    #renderTabBar = (thereAreShiftedElements) => (h("div", { role: "tablist", "aria-label": this.accessibleName, class: this.#classes.TAB_LIST, part: this.#parts.TAB_LIST, ref: el => (this.#tabListRef = el), onKeyDown: this.#handleTabFocus }, this.items.map((item, index) => (h("button", { key: CAPTION_ID(item.id), id: CAPTION_ID(item.id), role: "tab", "aria-controls": PAGE_ID(item.id), "aria-label": !this.#showCaptions ? item.name : null, "aria-selected": (item.id === this.selectedId).toString(), class: {
            [this.#classes.BUTTON]: true,
            "decorative-image": isDecorativeImg(item),
            "dragged-element": this.draggedElementIndex === index,
            "dragged-element--outside": this.draggedElementIndex === index &&
                this.hasCrossedBoundaries &&
                this.items.length > 1,
            "shifted-element": this.draggedElementIndex !== -1,
            "shifted-element--start": thereAreShiftedElements &&
                this.draggedElementIndex < index &&
                index <= this.draggedElementNewIndex,
            "shifted-element--end": thereAreShiftedElements &&
                this.draggedElementNewIndex <= index &&
                index < this.draggedElementIndex
        }, part: tokenMap({
            [this.#parts.BUTTON]: true,
            [CAPTION_ID(item.id)]: true,
            [SELECTED_PART]: item.id === this.selectedId
        }), style: isDecorativeImg(item)
            ? { [DECORATIVE_IMAGE]: `url("${item.startImageSrc}")` }
            : null, onAuxClick: this.#handleClose(index, item.id), onClick: !(item.id === this.selectedId)
            ? this.#handleSelectedItemChange(index, item.id)
            : null, onDblClick: this.type === "main" ? this.#handleItemDblClick : null,
        // Drag and drop
        onDragStart: !this.dragDisabled ? this.#handleDragStart(index) : null }, this.#imgRender(item), this.#showCaptions && item.name, this.#showCaptions && (h("button", { "aria-label": this.closeButtonAccessibleName, class: "close-button", part: CLOSE_BUTTON_PART, type: "button", onClick: this.#handleClose(index, item.id) })))))));
    #renderTabPages = () => (h("div", { class: {
            [this.#classes.PAGE_CONTAINER]: true,
            "page-container": true,
            "page-container--collapsed": !this.expanded
        }, part: this.#parts.PAGE_CONTAINER, ref: el => (this.#tabPageRef = el) }, [...this.#renderedPages.keys()].map(itemId => (h("div", { key: PAGE_ID(itemId), id: PAGE_ID(itemId), role: "tabpanel", "aria-labelledby": CAPTION_ID(itemId), class: {
            [this.#classes.PAGE]: true,
            "page--selected": itemId === this.selectedId,
            "page--hidden": !(itemId === this.selectedId)
        }, part: this.#parts.PAGE }, h("slot", { name: itemId }))))));
    #renderDragPreview = (draggedElement) => {
        const classes = {
            [DRAG_PREVIEW]: true,
            [DRAG_PREVIEW_OUTSIDE]: this.hasCrossedBoundaries,
            [DRAG_PREVIEW_INSIDE_INLINE]: !this.hasCrossedBoundaries && !isBlockDirection(this.#direction),
            [DRAG_PREVIEW_INSIDE_BLOCK]: !this.hasCrossedBoundaries && isBlockDirection(this.#direction)
        };
        return (h("div", { "aria-hidden": "true", class: classes, part: tokenMap(classes), ref: el => (this.#dragPreviewRef = el) }, h("button", { class: {
                [this.#classes.BUTTON]: true,
                [DRAG_PREVIEW_ELEMENT]: true,
                "decorative-image": isDecorativeImg(draggedElement)
            }, part: tokenMap({
                [this.#parts.BUTTON]: true,
                [CAPTION_ID(draggedElement.id)]: true,
                [DRAG_PREVIEW_ELEMENT]: true,
                [SELECTED_PART]: draggedElement.id === this.selectedId
            }), style: isDecorativeImg(draggedElement)
                ? { [DECORATIVE_IMAGE]: `url("${draggedElement.startImageSrc}")` }
                : null }, this.#imgRender(draggedElement), this.#showCaptions && draggedElement.name)));
    };
    #initializeState = (type) => {
        this.#direction = getDirection(type);
        this.#updateRenderedPages(this.items);
        // Initialize classes and parts
        this.#setClassesAndParts(this.#direction, type);
        this.#showCaptions = isBlockDirection(this.#direction);
    };
    #setClassesAndParts = (direction, type) => {
        this.#classes = {
            BUTTON: BUTTON_CLASS(direction),
            IMAGE: IMAGE_CLASS(direction),
            PAGE: PAGE_CLASS(direction),
            PAGE_CONTAINER: PAGE_CONTAINER_CLASS(direction),
            PAGE_NAME: PAGE_NAME_CLASS(direction),
            TAB_LIST: TAB_LIST_CLASS(direction)
        };
        // Add the type information to each part
        Object.entries(this.#classes).forEach(([key, value]) => {
            this.#parts[key] = `${value} ${type}`;
        });
    };
    componentWillLoad() {
        this.#initializeState(this.type);
    }
    render() {
        if (this.items == null || this.items.length === 0) {
            return "";
        }
        const draggedIndex = this.draggedElementIndex;
        const draggedElement = this.items[draggedIndex];
        const thereAreShiftedElementsInPreview = !this.hasCrossedBoundaries &&
            this.draggedElementNewIndex !== -1 &&
            this.draggedElementIndex !== this.draggedElementNewIndex;
        return (h(Host, { class: `ch-tab-direction--${this.#direction}` }, this.#renderTabBar(thereAreShiftedElementsInPreview), this.#renderTabPages(), draggedIndex !== -1 && this.#renderDragPreview(draggedElement)));
    }
    static get is() { return "ch-tab"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tab.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tab.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "closeButtonAccessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element. This label is used for the close button of the captions."
                },
                "attribute": "close-button-accessible-name",
                "reflect": false,
                "defaultValue": "\"Close\""
            },
            "dragDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drag operation is disabled in the\ncaptions of the control. If `true`, the captions can't be dragged."
                },
                "attribute": "drag-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "expanded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the group has is view section expanded. Otherwise, only the\ntoolbar will be displayed."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "true"
            },
            "items": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "FlexibleLayoutWidget[]",
                    "resolved": "FlexibleLayoutWidget[]",
                    "references": {
                        "FlexibleLayoutWidget": {
                            "location": "import",
                            "path": "../flexible-layout/types",
                            "id": "src/components/flexible-layout/types.ts::FlexibleLayoutWidget"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the items of the tab control."
                }
            },
            "selectedId": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the selected item of the widgets array."
                },
                "attribute": "selected-id",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TabType",
                    "resolved": "\"blockEnd\" | \"inlineEnd\" | \"inlineStart\" | \"main\"",
                    "references": {
                        "TabType": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tab/types.ts::TabType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the flexible layout type."
                },
                "attribute": "type",
                "reflect": true
            }
        };
    }
    static get states() {
        return {
            "draggedElementIndex": {},
            "draggedElementNewIndex": {},
            "hasCrossedBoundaries": {}
        };
    }
    static get events() {
        return [{
                "method": "expandMainGroup",
                "name": "expandMainGroup",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an item of the main group is double clicked."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "itemClose",
                "name": "itemClose",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired the close button of an item is clicked."
                },
                "complexType": {
                    "original": "TabItemCloseInfo",
                    "resolved": "{ itemId: string; itemIndex: number; type: TabType; }",
                    "references": {
                        "TabItemCloseInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tab/types.ts::TabItemCloseInfo"
                        }
                    }
                }
            }, {
                "method": "selectedItemChange",
                "name": "selectedItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected item change."
                },
                "complexType": {
                    "original": "TabSelectedItemInfo",
                    "resolved": "{ lastSelectedIndex: number; newSelectedId: string; newSelectedIndex: number; type: TabType; }",
                    "references": {
                        "TabSelectedItemInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tab/types.ts::TabSelectedItemInfo"
                        }
                    }
                }
            }, {
                "method": "itemDragStart",
                "name": "itemDragStart",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired the first time a caption button is dragged outside of its tab list."
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "endDragPreview": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Ends the preview of the dragged item. Useful for ending the preview via\nkeyboard interaction.",
                    "tags": []
                }
            },
            "getDraggableViews": {
                "complexType": {
                    "signature": "() => Promise<DraggableViewInfo>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "DraggableViewInfo": {
                            "location": "import",
                            "path": "../flexible-layout/types",
                            "id": "src/components/flexible-layout/types.ts::DraggableViewInfo"
                        }
                    },
                    "return": "Promise<DraggableViewInfo>"
                },
                "docs": {
                    "text": "Returns the info associated to the draggable view.",
                    "tags": []
                }
            },
            "promoteDragPreviewToTopLayer": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Promotes the drag preview to the top layer. Useful to avoid z-index issues.",
                    "tags": []
                }
            },
            "removePage": {
                "complexType": {
                    "signature": "(pageId: string, forceRerender?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "pageId",
                            "type": "string",
                            "docs": ""
                        }, {
                            "name": "forceRerender",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given an id, remove the page from the render",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "items",
                "methodName": "handleItemsChange"
            }, {
                "propName": "selectedId",
                "methodName": "handleSelectedIdChange"
            }, {
                "propName": "type",
                "methodName": "handleTypeChange"
            }];
    }
}
//# sourceMappingURL=tab.js.map
